import { Inject, Injectable } from '@angular/core';

import { HttpClient } from '@angular/common/http';
import * as angular from 'angular';
import { AppRootScope } from '../../src/typings/AppRootScope';
import { EventTypes } from '../../src/typings/models';
import { $http, $log, $q, $timeout } from '../ajs-upgraded-providers';
import { AppContextService } from '../appContext/AppContextService';
import { CommonBroadcastService } from '../common-broadcast/common-broadcast.service';
import { EndpointService } from '../endpoint/EndpointService';
import { NativeResponse } from './NativeResponse';
import { ProxyResponse } from './Response';
import { INativeResponse } from './typings/INativeResponse';
import { IProxyService } from './typings/IProxyService';
import { IResponse } from './typings/IResponse';
import { ApiRequest, RouteMetaData } from './typings/models';

// This is a custom service that delegates to the Angular $http service.
// The service does not depend on any hard-coded URLs and instead is
// injected with the route meta-data returned directly from the server
// API when the application is bootstrapped.
@Injectable({
    providedIn: 'root'
})
export class ProxyService implements IProxyService {
    // tslint:disable:member-ordering
    public static $name: string = 'proxyService';

    private _numPendingRequests = 0;
    private _isNotifyActive = false;

    constructor(
        @Inject('$rootScope') private $rootScope: AppRootScope,
        @Inject($timeout) private $timeout: angular.ITimeoutService,
        private appContextService: AppContextService,
        private endpointService: EndpointService,
        @Inject($log) private $log: angular.ILogService,
        @Inject($q) private $q: angular.IQService,
        @Inject($http) private $http: angular.IHttpService,
        private httpClient: HttpClient,
        private commonBroadcastService: CommonBroadcastService,
    ) {
        this.watchForLoadingNotification();
    }

    // Populates the $http config object based on the request information
    // and returns a response that can be used to access the results.
    public request(request: ApiRequest): IResponse {
        var route = this.getRoute(request);
        return this.callRoute(route, request);
    }

    public nativeRequest(request: ApiRequest): INativeResponse {
        var route = this.getRoute(request);
        return this.callNativeRoute(route, request);
    }

    // Lookup the route defined at a given endpoint.
    private getRoute(request: ApiRequest): RouteMetaData {
        const endpoints = this.endpointService.getEndpoints();
        var endpoint = endpoints[request.endPoint];

        if (!endpoint) {
            throw ({
                endpoint: request.endPoint,
                message: 'CASLink ProxyService **Endpoint** Not Configured',
                route: request.route
            });
        }

        var route = endpoint && endpoint.routes[request.route];
        if (!route) {
            throw ({
                endpoint: request.endPoint,
                message: 'CASLink ProxyService **Route** Not Configured',
                route: request.route
            });
        }

        return route;
    }
    public callRoute(
        route: RouteMetaData,
        request: ApiRequest): IResponse {

        // Populate Angular request-configuration using the route
        // metadata and the populated URL from the request data.
        var config: angular.IRequestConfig = {
            data: request.data,
            method: route.method,
            params: request.queryParams,
            timeout: request.timeout,
            url: this.buildUrl(request, route)
        };

        config.headers = request.headers || {};
        this.applyOptions(config, request.options);

        this.$log.debug({
            config: config,
            message: 'debug http request',
            request: request
        });

        // Return Response instance that will execute the query.
        // NOTE:  passing this many params goes against my religion. :)
        return new ProxyResponse(this.$rootScope, this.$q, this.$log, this.$http,
            config,
            request,
            (requestPromise: angular.IPromise<any>) => {

                const defaultDelay = 500;
                var delay = angular.isDefined(request.notificationDelay) ? request.notificationDelay : defaultDelay;

                if (delay !== -1) {
                    this.notifyLoading(delay, requestPromise);
                }
            });
    }

    public callNativeRoute(
        route: RouteMetaData,
        request: ApiRequest
    ): INativeResponse {
        /// Populate Angular request-configuration using the route
        // metadata and the populated URL from the request data.
        var config: angular.IRequestConfig = {
            data: request.data,
            method: route.method,
            params: request.queryParams,
            timeout: request.timeout,
            url: this.buildUrl(request, route)
        };

        config.headers = request.headers || {};
        this.applyOptions(config, request.options);

        this.$log.debug({
            config: config,
            message: 'debug http request',
            request: request
        });

        // Return Response instance that will execute the query.
        // NOTE:  passing this many params goes against my religion. :)
        return new NativeResponse(this.$rootScope, this.$log, this.httpClient,
            config,
            request,
            (requestPromise: Promise<any>) => {

                const defaultDelay = 500;
                var delay = angular.isDefined(request.notificationDelay) ? request.notificationDelay : defaultDelay;

                if (delay !== -1) {
                    this.notifyLoading(delay, requestPromise);
                }
            });
    }

    // Populate the URL route parameters from the specified routeParams and data.
    // Parameters specified on the routeParams take precedence over parameters
    // specified on data.
    private buildUrl(request: ApiRequest, route: RouteMetaData): string {

        var url = this.populateRouteParamsFromObject(request.routeParams, route.routeTemplate);
        url = this.populateRouteParamsFromObject(request.data, url);

        // remove any optional parameters from the route.
        route.paramNames.forEach((p: any) => {
            if (p.isOptional) {
                url = url.replace('/{' + p.name + '}', '');
            }
        });

        return url;
    }

    private populateRouteParamsFromObject(paramObj: any, routeTemplate: string): string {
        if (!paramObj) { return routeTemplate; }

        this.setCommonParamValues(paramObj);

        for (var p in paramObj) {
            var value = paramObj[p];

            if (paramObj.hasOwnProperty(p) && value !== null && typeof value !== 'function') {
                routeTemplate = routeTemplate.replace('{' + p + '}', value);
            }
        }
        return routeTemplate;
    }

    private setCommonParamValues(paramObj: any) {
        // Only automatically set the location value if it is not already specified.
        if (!angular.isDefined(paramObj.locationId) && this.appContextService.loadedLocationId) {
            /*
             * Don't append a locationId to an array...
             * this is only necessary because of the hacky stuff going on with Module Editor &
             * can be safely removed after CASLink-2998 is resolved
             */
            if (angular.isArray(paramObj)) {
                return;
            }

            paramObj.locationId = this.appContextService.loadedLocationId;
        }
    }

    // Adds additional options to the http configuration.
    private applyOptions(config: angular.IRequestConfig, options: any) {
        if (!options) { return; }

        for (var optionKey in options) {
            var option = options[optionKey];
            if (options.hasOwnProperty(optionKey) && option && typeof option !== 'function') {
                config[optionKey] = option;
            }
        }
    }

    private notifyLoading(
        delay: number,
        resultPromise: angular.IPromise<any>) {

        var countIncreased = false;

        var timeoutPromise = this.$timeout(() => {
            this._numPendingRequests++;
            countIncreased = true;
        }, delay);

        resultPromise.finally(() => {
            this.$timeout.cancel(timeoutPromise);

            if (countIncreased) {
                this._numPendingRequests--;
            }
        });
    }

    public watchForLoadingNotification() {
        this.$rootScope.$watch(
            () => this._numPendingRequests,
            (newCount: number) => {
                if (newCount === 0 && this._isNotifyActive) {
                    this._isNotifyActive = false;
                    this.$rootScope.publish(EventTypes.DisplayLoadIndicator, { isActive: false });
                    this.commonBroadcastService.broadcast(EventTypes.DisplayLoadIndicator, { isActive: false });

                } else if (newCount > 0 && !this._isNotifyActive) {
                    this._isNotifyActive = true;
                    this.$rootScope.publish(EventTypes.DisplayLoadIndicator, { isActive: true });
                    this.commonBroadcastService.broadcast(EventTypes.DisplayLoadIndicator, { isActive: true });
                }
            });
    }
}
